import { ArrowRight, PlayCircle, Star, Activity, Flame, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useReveal } from "@/lib/useReveal";
import { CountUp } from "./CountUp";

const heroImage =
  "https://images.unsplash.com/photo-1628935291759-bbaf33a66dc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxmaXRuZXNzJTIwdHJhaW5pbmclMjBkYXJrJTIwZ3ltJTIwbmVvbiUyMGxpZ2h0aW5nJTIwYXRobGV0ZXxlbnwwfHx8YmxhY2t8MTc3NzI5NTMyNnww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const leftRef = useReveal();
  const rightRef = useReveal({ threshold: 0.1 });

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 hero-backdrop noise-overlay pointer-events-none" aria-hidden="true" />
      <div
        className="absolute -top-20 -left-24 h-72 w-72 rounded-full blur-3xl opacity-40 orb-float-1"
        style={{ background: "radial-gradient(circle, rgba(191,255,0,0.55), transparent 60%)" }}
        aria-hidden="true"
      />
      <div
        className="absolute top-24 -right-20 h-80 w-80 rounded-full blur-3xl opacity-30 orb-float-2"
        style={{ background: "radial-gradient(circle, rgba(15,240,252,0.5), transparent 60%)" }}
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28">
        <div className="lg:grid lg:grid-cols-12 lg:gap-10 items-center">
          {/* Left */}
          <div ref={leftRef} className="reveal lg:col-span-7">
            <Badge
              data-testid="hero-eyebrow-badge"
              className="bg-white/5 border border-white/10 text-white/80 rounded-full px-3 py-1 text-xs font-medium hover:bg-white/10"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-neon-lime mr-2 pulse-dot inline-block" />
              New · Adaptive Training 2.0
            </Badge>

            <h1
              data-testid="hero-headline"
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white mt-5"
            >
              TRAIN SMART.
              <br />
              HIT <span className="text-neon-lime">PRs</span> FASTER.
            </h1>

            <p className="mt-5 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              Adaptive plans, real coaches, and a dashboard that proves your progress — week after week.
              Built for athletes who want results, not noise.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="#pricing">
                <Button
                  data-testid="hero-primary-cta-button"
                  className="btn-lift h-12 px-6 rounded-xl bg-neon-lime text-ink-0 hover:bg-neon-lime hover:brightness-95 font-semibold text-base shadow-glow-lime"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <a href="#workouts">
                <Button
                  variant="secondary"
                  data-testid="hero-secondary-cta-button"
                  className="btn-lift h-12 px-6 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-neon-cyan/40 font-medium"
                >
                  <PlayCircle className="mr-2 h-5 w-5 text-neon-cyan" />
                  Watch 30s Demo
                </Button>
              </a>
            </div>

            {/* Social proof */}
            <div data-testid="hero-social-proof" className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1.5">
                  {[0, 1, 2, 3].map((i) => (
                    <Star key={i} className="h-4 w-4 fill-neon-lime text-neon-lime" />
                  ))}
                  <Star className="h-4 w-4 fill-neon-lime/70 text-neon-lime/70" />
                </div>
                <span className="text-sm text-white/80">
                  <span className="font-semibold text-white">4.9</span> average rating
                </span>
              </div>
              <div className="h-4 w-px bg-white/10 hidden sm:block" />
              <div className="text-sm text-white/80">
                <span className="font-display text-2xl text-white align-middle">
                  <CountUp end={120} suffix="k+" />
                </span>{" "}
                <span className="align-middle">athletes</span>
              </div>
              <div className="h-4 w-px bg-white/10 hidden sm:block" />
              <div className="text-sm text-white/80">
                <span className="text-white/60">Featured in</span>{" "}
                <span className="text-white/90 font-semibold">Men&rsquo;s Health</span>
                <span className="text-white/40 mx-1">·</span>
                <span className="text-white/90 font-semibold">Wired</span>
              </div>
            </div>
          </div>

          {/* Right visual */}
          <div ref={rightRef} className="reveal lg:col-span-5 mt-12 lg:mt-0">
            <div className="relative">
              {/* Phone / athlete visual */}
              <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-ink-1 shadow-soft">
                <img
                  src={heroImage}
                  alt="Athlete training in dark gym with neon lighting"
                  className="w-full h-[420px] sm:h-[460px] object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-0 via-ink-0/30 to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2 rounded-full bg-black/50 border border-white/10 px-3 py-1 text-xs text-white">
                  <span className="h-1.5 w-1.5 rounded-full bg-neon-lime pulse-dot" />
                  Session · Upper Body
                </div>
                {/* Floating stat card 1 */}
                <div className="absolute -left-3 bottom-20 sm:left-4 rounded-2xl glass px-4 py-3 shadow-card">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-neon-lime/15 border border-neon-lime/30 flex items-center justify-center">
                      <Flame className="h-4 w-4 text-neon-lime" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60">Calories</div>
                      <div className="font-mono-num text-lg font-semibold text-white leading-none">
                        <CountUp end={642} /> kcal
                      </div>
                    </div>
                  </div>
                </div>
                {/* Floating stat card 2 */}
                <div className="absolute right-4 top-24 rounded-2xl glass px-4 py-3 shadow-card">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-neon-cyan/15 border border-neon-cyan/30 flex items-center justify-center">
                      <Activity className="h-4 w-4 text-neon-cyan" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60">Heart rate</div>
                      <div className="font-mono-num text-lg font-semibold text-white leading-none">
                        <CountUp end={152} /> bpm
                      </div>
                    </div>
                  </div>
                </div>
                {/* Bottom session bar */}
                <div className="absolute left-4 right-4 bottom-4 rounded-2xl glass px-4 py-3 shadow-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60">Today</div>
                      <div className="text-sm text-white font-semibold">Push Day · Week 3</div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/80">
                      <Timer className="h-3.5 w-3.5" /> 42:18
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full w-[68%] rounded-full bg-gradient-to-r from-neon-lime to-neon-cyan" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="relative border-y border-white/5 bg-white/[0.02] py-4 overflow-hidden">
        <div className="marquee-track flex gap-10 whitespace-nowrap text-white/40 text-sm tracking-[0.3em] uppercase">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-10 px-5">
              <span>Adaptive Training</span>
              <span className="text-neon-lime">✦</span>
              <span>Progress Analytics</span>
              <span className="text-neon-cyan">✦</span>
              <span>Certified Coaches</span>
              <span className="text-neon-orange">✦</span>
              <span>PR Tracking</span>
              <span className="text-neon-lime">✦</span>
              <span>Recovery Plans</span>
              <span className="text-neon-cyan">✦</span>
              <span>Nutrition Macros</span>
              <span className="text-neon-orange">✦</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
